import { useEffect, useState, type ReactElement } from 'react';

import type { Localization } from '../localization/locale.ts';
import {
  createBrowserTrailStatusEnvironment,
  createTrailStatusController,
  type TrailStatusController,
  type TrailStatusState,
} from './trail-status.ts';

export interface TrailStatusProps {
  readonly localization: Localization;
  readonly controller?: TrailStatusController;
}

export function TrailStatus({ localization, controller }: TrailStatusProps): ReactElement {
  const [state, setState] = useState<TrailStatusState>({ type: 'loading' });
  const [activeController, setActiveController] = useState<TrailStatusController | undefined>(
    controller,
  );

  useEffect(() => {
    const mountedController =
      controller ?? createTrailStatusController(createBrowserTrailStatusEnvironment(window));
    setActiveController(mountedController);
    const unsubscribe = mountedController.subscribe(setState);
    return () => {
      unsubscribe();
      if (controller === undefined) mountedController.destroy();
    };
  }, [controller]);

  return (
    <div
      className="trail-status"
      aria-label={localization.message('branding.trailStatus.label')}
      aria-busy={state.type === 'loading'}
    >
      {state.imageUrl !== undefined ? (
        <img src={state.imageUrl} alt={localization.message('branding.trailStatus.imageAlt')} />
      ) : null}
      <span
        className={state.type === 'unavailable' ? undefined : 'visually-hidden'}
        aria-live="polite"
      >
        {localization.message(
          state.type === 'loading'
            ? 'branding.trailStatus.loading'
            : state.type === 'ready'
              ? 'branding.trailStatus.ready'
              : 'branding.trailStatus.unavailable',
        )}
      </span>
      {state.type === 'unavailable' ? (
        <button type="button" onClick={activeController?.retry}>
          {localization.message('errors.retry')}
        </button>
      ) : null}
    </div>
  );
}
